import React, { useState, useEffect, useRef } from "react";
import {
	Clock,
	Play,
	Pause,
	RotateCcw,
	Flag,
	Settings,
	Maximize,
	Minimize,
} from "lucide-react";

const Timer = () => {
	const [timerMode, setTimerMode] = useState("select"); // select, simple, tabata
	const [isRunning, setIsRunning] = useState(false);
	const [isPaused, setIsPaused] = useState(false);
	const [time, setTime] = useState(0);
	const [laps, setLaps] = useState([]);
	const [isFullscreen, setIsFullscreen] = useState(false);
	const intervalRef = useRef(null);
	const timerContainerRef = useRef(null);

	// Tabata settings
	const [tabataSettings, setTabataSettings] = useState({
		countdown: 10,
		warmup: 30,
		exercise: 20,
		rest: 10,
		sets: 8,
		recovery: 60,
		cycles: 3,
		cooldown: 30,
	});

	const [tabataState, setTabataState] = useState({
		phase: "countdown", // countdown, warmup, exercise, rest, recovery, cooldown, complete
		currentSet: 1,
		currentCycle: 1,
		phaseTime: 0,
	});

	const [showSettings, setShowSettings] = useState(false);

	// CSS-based fullscreen (works on iOS Safari unlike the Fullscreen API)
	const toggleFullscreen = () => {
		setIsFullscreen((prev) => !prev);
	};

	// Lock body scroll when fullscreen is active
	useEffect(() => {
		if (isFullscreen) {
			document.body.style.overflow = "hidden";
		} else {
			document.body.style.overflow = "";
		}
		return () => {
			document.body.style.overflow = "";
		};
	}, [isFullscreen]);

	useEffect(() => {
		if (isRunning && !isPaused) {
			if (timerMode === "simple") {
				intervalRef.current = setInterval(() => {
					setTime((prev) => prev + 10);
				}, 10);
			} else if (timerMode === "tabata") {
				intervalRef.current = setInterval(() => {
					handleTabataTimer();
				}, 10);
			}
		} else {
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
			}
		}

		return () => {
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
			}
		};
	}, [isRunning, isPaused, timerMode]);

	const handleTabataTimer = () => {
		setTabataState((prev) => {
			const newPhaseTime = prev.phaseTime + 10;
			let newState = { ...prev, phaseTime: newPhaseTime };

			const phaseDuration = getTabataPhaseDuration(prev.phase);

			if (newPhaseTime >= phaseDuration) {
				// Move to next phase
				const nextPhase = getNextTabataPhase(prev);
				newState = { ...nextPhase, phaseTime: 0 };

				// Play sound notification
				playBeep();
			}

			return newState;
		});
	};

	const getTabataPhaseDuration = (phase) => {
		const durations = {
			countdown: tabataSettings.countdown * 1000,
			warmup: tabataSettings.warmup * 1000,
			exercise: tabataSettings.exercise * 1000,
			rest: tabataSettings.rest * 1000,
			recovery: tabataSettings.recovery * 1000,
			cooldown: tabataSettings.cooldown * 1000,
			complete: 0,
		};
		return durations[phase] || 0;
	};

	const getNextTabataPhase = (current) => {
		const { phase, currentSet, currentCycle } = current;

		if (phase === "countdown") {
			return { ...current, phase: "warmup" };
		}

		if (phase === "warmup") {
			return { ...current, phase: "exercise" };
		}

		if (phase === "exercise") {
			if (currentSet < tabataSettings.sets) {
				return { ...current, phase: "rest" };
			} else {
				// End of sets in this cycle
				if (currentCycle < tabataSettings.cycles) {
					return { ...current, phase: "recovery", currentSet: 1 };
				} else {
					return { ...current, phase: "cooldown" };
				}
			}
		}

		if (phase === "rest") {
			return { ...current, phase: "exercise", currentSet: currentSet + 1 };
		}

		if (phase === "recovery") {
			return { ...current, phase: "exercise", currentCycle: currentCycle + 1 };
		}

		if (phase === "cooldown") {
			setIsRunning(false);
			return { ...current, phase: "complete" };
		}

		return current;
	};

	const playBeep = () => {
		const audioContext = new (
			window.AudioContext || window.webkitAudioContext
		)();
		const oscillator = audioContext.createOscillator();
		const gainNode = audioContext.createGain();

		oscillator.connect(gainNode);
		gainNode.connect(audioContext.destination);

		oscillator.frequency.value = 800;
		oscillator.type = "sine";

		gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
		gainNode.gain.exponentialRampToValueAtTime(
			0.01,
			audioContext.currentTime + 0.1,
		);

		oscillator.start(audioContext.currentTime);
		oscillator.stop(audioContext.currentTime + 0.1);
	};

	const formatTime = (milliseconds) => {
		const totalSeconds = Math.floor(milliseconds / 1000);
		const minutes = Math.floor(totalSeconds / 60);
		const seconds = totalSeconds % 60;
		const ms = Math.floor((milliseconds % 1000) / 10);
		return `${minutes.toString().padStart(2, "0")}:${seconds
			.toString()
			.padStart(2, "0")}.${ms.toString().padStart(2, "0")}`;
	};

	const formatTabataTime = (milliseconds) => {
		const totalSeconds = Math.floor(milliseconds / 1000);
		const minutes = Math.floor(totalSeconds / 60);
		const seconds = totalSeconds % 60;
		return `${minutes.toString().padStart(2, "0")}:${seconds
			.toString()
			.padStart(2, "0")}`;
	};

	const handleStart = () => {
		setIsRunning(true);
		setIsPaused(false);
	};

	const handleResume = () => {
		setIsPaused(false);
	};

	const handleStop = () => {
		// Stop just pauses the timer — does NOT reset
		setIsPaused(true);
	};

	const handleReset = () => {
		setIsRunning(false);
		setIsPaused(false);
		setTime(0);
		setLaps([]);
		setTabataState({
			phase: "countdown",
			currentSet: 1,
			currentCycle: 1,
			phaseTime: 0,
		});
	};

	const handleLap = () => {
		setLaps([...laps, time]);
	};

	const handleStartTabata = () => {
		setShowSettings(false);
		setTabataState({
			phase: "countdown",
			currentSet: 1,
			currentCycle: 1,
			phaseTime: 0,
		});
		setIsRunning(true);
		setIsPaused(false);
	};

	const getTabataPhaseColor = (phase) => {
		const colors = {
			countdown: "#6c757d",
			warmup: "#ffc107",
			exercise: "#28a745",
			rest: "#17a2b8",
			recovery: "#fd7e14",
			cooldown: "#6f42c1",
			complete: "#6c757d",
		};
		return colors[phase] || "#6c757d";
	};

	const getTabataPhaseLabel = (phase) => {
		const labels = {
			countdown: "Get Ready",
			warmup: "Warm Up",
			exercise: "Exercise",
			rest: "Rest",
			recovery: "Recovery",
			cooldown: "Cool Down",
			complete: "Complete!",
		};
		return labels[phase] || phase;
	};

	const renderModeSelection = () => (
		<div className="timer-mode-selection">
			<h5 className="mb-4 text-center">Choose Timer Mode</h5>
			<div className="row g-3">
				<div className="col-md-6">
					<button
						className="timer-mode-card"
						onClick={() => setTimerMode("simple")}
					>
						<Clock size={48} className="mb-3" />
						<h6>Simple Timer</h6>
						<p className="text-muted small mb-0">
							Basic stopwatch with lap tracking
						</p>
					</button>
				</div>
				<div className="col-md-6">
					<button
						className="timer-mode-card"
						onClick={() => {
							setTimerMode("tabata");
							setShowSettings(true);
						}}
					>
						<Settings size={48} className="mb-3" />
						<h6>Tabata Timer</h6>
						<p className="text-muted small mb-0">
							Interval training with custom settings
						</p>
					</button>
				</div>
			</div>
		</div>
	);

	const renderSimpleTimer = () => (
		<div className="timer-display-container">
			<div className="timer-header">
				<h5>Simple Timer</h5>
				<div className="d-flex gap-2">
					<button
						className="btn btn-sm btn-outline-secondary"
						onClick={toggleFullscreen}
						title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
					>
						{isFullscreen ? <Minimize size={16} /> : <Maximize size={16} />}
					</button>
					<button
						className="btn btn-sm btn-outline-secondary"
						onClick={() => {
							handleReset();
							setTimerMode("select");
						}}
					>
						Change Mode
					</button>
				</div>
			</div>

			<div className="timer-display">
				<div className="timer-time">{formatTime(time)}</div>
			</div>

			<div className="timer-controls">
				{!isRunning ? (
					<button className="timer-btn timer-btn-primary" onClick={handleStart}>
						<Play size={24} />
						<span>Start</span>
					</button>
				) : isPaused ? (
					<>
						<button
							className="timer-btn timer-btn-success"
							onClick={handleResume}
						>
							<Play size={24} />
							<span>Resume</span>
						</button>
						<button className="timer-btn timer-btn-info" onClick={handleLap}>
							<Flag size={24} />
							<span>Lap</span>
						</button>
						<button
							className="timer-btn timer-btn-danger"
							onClick={handleReset}
						>
							<RotateCcw size={24} />
							<span>Reset</span>
						</button>
					</>
				) : (
					<>
						<button
							className="timer-btn timer-btn-warning"
							onClick={handleStop}
						>
							<Pause size={24} />
							<span>Stop</span>
						</button>
						<button className="timer-btn timer-btn-info" onClick={handleLap}>
							<Flag size={24} />
							<span>Lap</span>
						</button>
					</>
				)}
			</div>

			{laps.length > 0 && (
				<div className="timer-laps">
					<h6>Laps</h6>
					<div className="laps-list">
						{laps.map((lap, index) => (
							<div key={index} className="lap-item">
								<span className="lap-number">Lap {index + 1}</span>
								<span className="lap-time">{formatTime(lap)}</span>
								<span className="lap-diff text-muted">
									{index > 0 ? `+${formatTime(lap - laps[index - 1])}` : "-"}
								</span>
							</div>
						))}
					</div>
				</div>
			)}
		</div>
	);

	const renderTabataSettings = () => (
		<div className="tabata-settings">
			<h5 className="mb-4">Tabata Timer Settings</h5>
			<div className="row g-3">
				<div className="col-md-6">
					<label className="form-label">Countdown (seconds)</label>
					<input
						type="number"
						className="form-control"
						value={tabataSettings.countdown}
						onChange={(e) =>
							setTabataSettings({
								...tabataSettings,
								countdown: parseInt(e.target.value) || 0,
							})
						}
						min="0"
						max="60"
					/>
				</div>
				<div className="col-md-6">
					<label className="form-label">Warm Up (seconds)</label>
					<input
						type="number"
						className="form-control"
						value={tabataSettings.warmup}
						onChange={(e) =>
							setTabataSettings({
								...tabataSettings,
								warmup: parseInt(e.target.value) || 0,
							})
						}
						min="0"
						max="300"
					/>
				</div>
				<div className="col-md-6">
					<label className="form-label">Exercise Interval (seconds)</label>
					<input
						type="number"
						className="form-control"
						value={tabataSettings.exercise}
						onChange={(e) =>
							setTabataSettings({
								...tabataSettings,
								exercise: parseInt(e.target.value) || 0,
							})
						}
						min="1"
						max="300"
					/>
				</div>
				<div className="col-md-6">
					<label className="form-label">Rest Interval (seconds)</label>
					<input
						type="number"
						className="form-control"
						value={tabataSettings.rest}
						onChange={(e) =>
							setTabataSettings({
								...tabataSettings,
								rest: parseInt(e.target.value) || 0,
							})
						}
						min="0"
						max="300"
					/>
				</div>
				<div className="col-md-6">
					<label className="form-label">Sets per Cycle</label>
					<input
						type="number"
						className="form-control"
						value={tabataSettings.sets}
						onChange={(e) =>
							setTabataSettings({
								...tabataSettings,
								sets: parseInt(e.target.value) || 1,
							})
						}
						min="1"
						max="20"
					/>
				</div>
				<div className="col-md-6">
					<label className="form-label">Recovery (seconds)</label>
					<input
						type="number"
						className="form-control"
						value={tabataSettings.recovery}
						onChange={(e) =>
							setTabataSettings({
								...tabataSettings,
								recovery: parseInt(e.target.value) || 0,
							})
						}
						min="0"
						max="300"
					/>
				</div>
				<div className="col-md-6">
					<label className="form-label">Number of Cycles</label>
					<input
						type="number"
						className="form-control"
						value={tabataSettings.cycles}
						onChange={(e) =>
							setTabataSettings({
								...tabataSettings,
								cycles: parseInt(e.target.value) || 1,
							})
						}
						min="1"
						max="10"
					/>
				</div>
				<div className="col-md-6">
					<label className="form-label">Cool Down (seconds)</label>
					<input
						type="number"
						className="form-control"
						value={tabataSettings.cooldown}
						onChange={(e) =>
							setTabataSettings({
								...tabataSettings,
								cooldown: parseInt(e.target.value) || 0,
							})
						}
						min="0"
						max="300"
					/>
				</div>
			</div>
			<div className="mt-4 d-flex gap-2">
				<button
					className="btn btn-primary flex-grow-1"
					onClick={handleStartTabata}
				>
					Start Tabata Timer
				</button>
				<button
					className="btn btn-outline-secondary"
					onClick={() => {
						setTimerMode("select");
						setShowSettings(false);
					}}
				>
					Cancel
				</button>
			</div>
		</div>
	);

	const renderTabataTimer = () => {
		const phaseDuration = getTabataPhaseDuration(tabataState.phase);
		const remainingTime = phaseDuration - tabataState.phaseTime;
		const progress = (tabataState.phaseTime / phaseDuration) * 100;

		return (
			<div className="timer-display-container">
				<div className="timer-header">
					<h5>Tabata Timer</h5>
					<div className="d-flex gap-2">
						<button
							className="btn btn-sm btn-outline-secondary"
							onClick={toggleFullscreen}
							title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
						>
							{isFullscreen ? <Minimize size={16} /> : <Maximize size={16} />}
						</button>
						<button
							className="btn btn-sm btn-outline-secondary"
							onClick={() => setShowSettings(true)}
						>
							<Settings size={16} />
						</button>
					</div>
				</div>

				<div
					className="tabata-phase-indicator"
					style={{ backgroundColor: getTabataPhaseColor(tabataState.phase) }}
				>
					<h3>{getTabataPhaseLabel(tabataState.phase)}</h3>
					{tabataState.phase !== "complete" &&
						tabataState.phase !== "countdown" &&
						tabataState.phase !== "warmup" &&
						tabataState.phase !== "cooldown" && (
							<p className="mb-0">
								Cycle {tabataState.currentCycle} / {tabataSettings.cycles} • Set{" "}
								{tabataState.currentSet} / {tabataSettings.sets}
							</p>
						)}
				</div>

				<div className="timer-display">
					<div className="timer-time tabata-time">
						{formatTabataTime(remainingTime)}
					</div>
					<div className="tabata-progress-bar">
						<div
							className="tabata-progress-fill"
							style={{
								width: `${progress}%`,
								backgroundColor: getTabataPhaseColor(tabataState.phase),
							}}
						/>
					</div>
				</div>

				<div className="timer-controls">
					{!isRunning || tabataState.phase === "complete" ? (
						<>
							<button
								className="timer-btn timer-btn-primary"
								onClick={handleStartTabata}
							>
								<Play size={24} />
								<span>Start</span>
							</button>
							<button
								className="timer-btn timer-btn-secondary"
								onClick={() => {
									handleReset();
									setTimerMode("select");
								}}
							>
								<RotateCcw size={24} />
								<span>Change Mode</span>
							</button>
						</>
					) : isPaused ? (
						<>
							<button
								className="timer-btn timer-btn-success"
								onClick={handleResume}
							>
								<Play size={24} />
								<span>Resume</span>
							</button>
							<button
								className="timer-btn timer-btn-danger"
								onClick={handleReset}
							>
								<RotateCcw size={24} />
								<span>Reset</span>
							</button>
						</>
					) : (
						<>
							<button
								className="timer-btn timer-btn-warning"
								onClick={handleStop}
							>
								<Pause size={24} />
								<span>Stop</span>
							</button>
						</>
					)}
				</div>
			</div>
		);
	};

	return (
		<div
			className={`timer-container ${isFullscreen ? "timer-fullscreen" : ""}`}
			ref={timerContainerRef}
		>
			{timerMode === "select" && renderModeSelection()}
			{timerMode === "simple" && renderSimpleTimer()}
			{timerMode === "tabata" &&
				(showSettings ? renderTabataSettings() : renderTabataTimer())}
		</div>
	);
};

export default Timer;
